import React from "react";

import { cn } from "../../../common/u1/cn";
import { CheckIcon } from "../../../../assets/ui/icons";

/* One thing you can buy, and whether it is the one selected. */
export const PlanOptionCard = ({
  id,
  name = "u1-plan",
  title,
  description,
  features = [],
  price,
  priceSuffix,
  strikePrice,
  footnote,
  ribbon,
  badge,
  selected = false,
  selectable = true,
  disabled = false,
  note,
  tooltip,
  onSelect,
  children,
  className = "",
}) => {
  const choosable = selectable && !disabled;
  /* A div rather than a label once it is disabled: a label whose input cannot
     be activated is a click target that does nothing, and `note` may carry a
     link, which does not belong inside a label. */
  const Wrapper = choosable ? "label" : "div";
  const tipId = tooltip && id ? `${id}-tip` : undefined;

  const card = (
    <Wrapper
      htmlFor={choosable ? id : undefined}
      aria-disabled={disabled || undefined}
      aria-describedby={tipId}
      tabIndex={tooltip ? 0 : undefined}
      className={cn(
        "relative flex h-full flex-col rounded-[14px] border-2 p-5 sm:p-6 text-left",
        "transition-colors",
        choosable && "cursor-pointer",
        disabled
          ? "cursor-not-allowed border-[var(--line)] bg-[var(--slate-2)]"
          : "bg-white",
        selected
          ? "border-[var(--coral)]"
          : !disabled && "border-[var(--line)] hover:border-[var(--line-2)]",
        className
      )}
    >
      {ribbon && (
        <span
          className={cn(
            "absolute -top-3 left-5 rounded-md px-2 py-1",
            "text-[10px] font-bold uppercase tracking-[.08em] text-white font-sans",
            disabled ? "bg-[var(--muted-2)]" : "bg-[var(--navy)]"
          )}
        >
          {ribbon}
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <h2
          className={cn(
            "m-0 text-[16px] font-bold font-sans",
            disabled ? "text-[var(--muted)]" : "text-[var(--ink)]"
          )}
        >
          {title}
        </h2>

        {badge && (
          <span
            className={cn(
              "shrink-0 rounded-md bg-[var(--coral-soft)] px-2 py-1",
              "text-[11px] font-semibold text-[var(--coral-deep)] font-sans"
            )}
          >
            {badge}
          </span>
        )}

        {selectable && (
          <>
            <input
              type="radio"
              id={id}
              name={name}
              checked={selected}
              disabled={disabled}
              onChange={() => onSelect?.()}
              className="sr-only"
            />
            <span
              aria-hidden="true"
              className={cn(
                "flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors",
                selected
                  ? "border-[var(--coral)] bg-[var(--coral)] text-white"
                  : "border-[var(--line-2)] bg-white",
                disabled && "border-[var(--line-2)] bg-[var(--line)]"
              )}
            >
              {selected && <CheckIcon size={11} />}
            </span>
          </>
        )}
      </div>

      {description && (
        <p className="m-0 mt-1.5 text-[13px] leading-[1.55] text-[var(--muted)] font-sans">
          {description}
        </p>
      )}

      {features.length > 0 && (
        <ul className="m-0 mt-3.5 flex list-none flex-col gap-2 p-0">
          {features.map((feature) => (
            <li
              key={feature}
              className="flex gap-2 text-[13px] leading-[1.5] text-[var(--ink-2)] font-sans"
            >
              <span
                aria-hidden="true"
                className={cn(
                  "mt-[3px]",
                  disabled ? "text-[var(--muted-2)]" : "text-[var(--coral)]"
                )}
              >
                <CheckIcon size={11} />
              </span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      )}

      {/* mt-auto keeps the price on the baseline in a two-card row whose
          descriptions are different lengths. */}
      <div className="mt-auto pt-4">
        <div className="flex flex-wrap items-baseline gap-2">
          <span
            className={cn(
              "text-[22px] font-bold font-sans",
              disabled ? "text-[var(--muted)]" : "text-[var(--ink)]"
            )}
          >
            {price}
          </span>
          {priceSuffix && (
            <span className="text-[13px] text-[var(--muted)] font-sans">
              {priceSuffix}
            </span>
          )}
          {strikePrice && (
            <span className="text-[13px] text-[var(--muted)] line-through font-sans">
              {strikePrice}
            </span>
          )}
        </div>
        {footnote && (
          <p className="m-0 mt-1 text-[11.5px] text-[var(--muted)] font-sans">
            {footnote}
          </p>
        )}
      </div>

      {/* The reason it cannot be chosen. A disabled control that does not say
          why is the thing people file bugs about. */}
      {note && (
        <p className="m-0 mt-3 text-[11.5px] leading-[1.5] text-[var(--muted)] font-sans">
          {note}
        </p>
      )}

      {/* Whatever sits below the price — the membership term toggle — is part
          of choosing, so it goes away with the choice. */}
      {!disabled && children}
    </Wrapper>
  );

  if (!tooltip) return card;

  return (
    /* `group` drives the bubble from hover on the card and from focus anywhere
       inside it, so there is no open/close state to keep. */
    <div className="group relative h-full">
      {card}
      <span
        id={tipId}
        role="tooltip"
        className={cn(
          "pointer-events-none absolute bottom-full left-1/2 z-20 mb-2 w-max max-w-[260px]",
          "-translate-x-1/2 rounded-lg bg-[var(--navy)] px-3 py-2 text-center",
          "text-[11.5px] leading-[1.45] text-white font-sans",
          "opacity-0 transition-opacity duration-150",
          "group-hover:opacity-100 group-focus-within:opacity-100"
        )}
      >
        {tooltip}
      </span>
    </div>
  );
};

export default PlanOptionCard;
